import {IFormulaObserver} from '~/utils/observers/formulaObserver'
import {IRulesObserver} from '~/utils/observers/rulesObserver'
import {IValidationObserver} from '~/utils/observers/validationObserver'
import {IWysiwygObserver} from '~/utils/observers/wysiwygObserver'
import {IFileObserver} from '~/utils/observers/fileObserver'
import {IDynamicIdComponents} from '~/interfaces/dynamicFieldsInterfaces'
import {getFormulaVariables} from '~/utils/formulaEngineUtils'
import {parseFormula} from 'formula-engine'
import Formula from '~/models/formula'
import {IClientIdable} from '~/interfaces/propertyInterfaces'

export interface IObserverOptionsCommon<M> {
  id: string
  model: M

  callback(): void
}

export interface IObserver<M> {
  id: string
  model: M
  observers: Record<string, IObserver<M>>
  registered: boolean
  readonly type: string

  callback(): void

  react(): void
}

export type TObserverSpecific<M> =
  IRulesObserver<M>
  | IFormulaObserver<M>
  | IValidationObserver<M>
  | IWysiwygObserver<M>
  | IFileObserver<M>

export default abstract class Observer<M, Opts extends IObserverOptionsCommon<M>> implements IObserver<M> {
  /**
   * Observers can have sub-observers
   * @example RulesObserver can have FormulaObserver
   */
  observers: Record<string, IObserver<M>> = {}
  /**
   * Has this observer been registered with an ObserverGroup? (or whatever else we may hook it up to)
   * Used to prevent double registering an observer
   */
  registered: boolean = false
  abstract type: string

  /**
   * Generic function for executing a function if the value of the property is a function
   * or otherwise returning the value of the property
   *
   * @param options
   * @protected
   */
  protected constructor(protected options: Opts) {
  }

  /**
   * Unique identifier of this Observer.
   * Can be anything unique. I've been using it as a description.
   */
  get id(): string {
    return this.call("id")
  }

  get callback() {
    return this.options.callback
  }

  get model(): M {
    return this.call("model")
  }

  /**
   * Returns all dependent client_ids found
   * Mutates Observers by creating sub-observers when necessary
   * @param observer
   * @param opts
   * @param prepOpts
   */
  static prepareObserver<M extends IClientIdable>(observer: TObserverSpecific<M>, opts: { toFormulaIdComponents: (variableName: string) => IDynamicIdComponents | null }, prepOpts: { createSubObservers: boolean }): string[] {
    const clientIds: Set<string> = new Set()

    switch (observer.type) {
      case 'formula':
        if (observer.formula) {
          getFormulaVariables(parseFormula(observer.formula)).forEach(variable => {
            const components = opts.toFormulaIdComponents(variable.name)
            if (components?.id) clientIds.add(Formula.expandFlatClientId(components.id))
          })
        }
        break
      case 'rules':
        observer.forEachRuleAndSet((params) => {
          if ('ruleSet' in params) {
            const ruleSet = params.ruleSet
            if (prepOpts.createSubObservers && ruleSet.conditional_value_formula) {
              observer.createFormulaObserver({ formula: ruleSet.conditional_value_formula })
            }
          } else if ('rule' in params) {
            const rule = params.rule
            if (rule.referenced_control_id) clientIds.add(rule.referenced_control_id)
          }
        })
        if (prepOpts.createSubObservers && observer.formula) {
          observer.createFormulaObserver({ formula: observer.formula })
        }
        break
      case 'wysiwyg':
        observer.clientIds.forEach(clientIds.add, clientIds)
        break
      case 'validation':
        clientIds.add(observer.model.client_id)
        break
      case 'file':
        clientIds.add(observer.model.client_id)
        break
    }
    return Array.from(clientIds)
  }

  react(): void {
    this.callback()
  }

  protected formulaNameFrom(fieldName: string): string {
    return fieldName + '_formula'
  }

  protected call<K extends keyof Opts>(prop: K): Opts[K] {
    const value = (<any>this.options)[prop]
    return isFunction<Opts[K]>(value) ? value() : value
  }
}

/**
 * Basic user type guard function to check if something is a function
 * @param value
 */
function isFunction<V>(value: unknown): value is (...args) => V {
  return typeof value === 'function'
}
